import { useCallback } from "react";
import * as R from "ramda";
import { useFormState } from "react-hook-form";

const makeDirtyFieldsSelector = R.compose(R.pickAll, R.keys, R.filter(R.identity));

export function useFormHandler({ handler, formControl, onlyDirtyFields = true }) {
  const { dirtyFields } = useFormState({ control: formControl });
  const filter = onlyDirtyFields ? makeDirtyFieldsSelector(dirtyFields) : R.identity;
  return useCallback(
    async (data) => {
      try {
        return await handler(filter(data));
      } catch (e) {
        console.error("error in useFormHandler", e);
        return null;
      }
    },
    [handler, filter],
  );
}

/**
 * Standard error handler for forms that associates rails generated errors with form inputs
 *
 * Flattens rails multi error messages into a single "," seperated string
 * @param {function} setError - react-hook-form setError func
 * @returns {function} - the fn that does the backend request. likely a useMutation().mutateAsync fn
 */
export const errorHandler =
  (setError) =>
  ({ response }) => {
    if (response?.status !== 422) return;

    Object.entries(response?.data?.errors).forEach(([attr, msgs]) => {
      const msg = Array.isArray(msgs) ? msgs.join(", ") : msgs;
      setError(attr, { type: "server", message: msg });
    });
  };

/**
 * Wraps input function to silently swallow errors, for use with react-hook-form. We usually use react-query mutations and the mutation catches errors and exposes them on the onError callback, while RHF expects the fn given to handleSubmit to return without throwing if the backend submit completed (with or without error).
 *
 * @param {function} fn - the fn that does the backend request. likely a useMutation().mutateAsync fn
 * @returns {function} - a wrapped version of the input fn that silently swallows errors. Probably given to RHF's useForm().handleSubmit
 */
export const swallowErrors =
  (fn) =>
  async (...args) => {
    try {
      return await fn(...args);
    } catch (e) {
      return null;
    }
  };
